import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasValue } from '@progress-fe/core';
import { NUMBERS } from '../constants';
import { useOsSettings } from './useOsSettings';
const SKIP_KEYS = ['capslock'];
const useKeyPress = () => {
    const keys = useRef(new Set());
    const [pressedKeys, setPressedKeys] = useState(new Set());
    const { deleteKeyCode } = useOsSettings();
    const keysAmount = useMemo(() => Array.from(pressedKeys).length, [pressedKeys]);
    const pressedNumber = useMemo(() => {
        const numberAsStr = Array.from(pressedKeys).find((k) => NUMBERS.includes(k));
        return hasValue(numberAsStr) ? Number(numberAsStr) : null;
    }, [pressedKeys]);
    // FYI: Project import
    const isControlIPressed = useMemo(() => {
        return keysAmount === 2 && pressedKeys.has('control') && pressedKeys.has('i');
    }, [pressedKeys, keysAmount]);
    // FYI: Project export
    const isControlEPressed = useMemo(() => {
        return keysAmount === 2 && pressedKeys.has('control') && pressedKeys.has('e');
    }, [pressedKeys, keysAmount]);
    // FYI: Toggle journal
    const isControlLPressed = useMemo(() => {
        return keysAmount === 2 && pressedKeys.has('control') && pressedKeys.has('l');
    }, [pressedKeys, keysAmount]);
    // FYI: Switch tabs in js-forms
    const isControlNumberPressed = useMemo(() => {
        return keysAmount === 2 && pressedKeys.has('control') && hasValue(pressedNumber);
    }, [pressedKeys, keysAmount, pressedNumber]);
    // FYI: Zone: Go to Structure
    const isF1Pressed = useMemo(() => {
        return keysAmount === 1 && pressedKeys.has('f1');
    }, [pressedKeys, keysAmount]);
    // FYI: Zone: Go to Calculation
    const isF2Pressed = useMemo(() => {
        return keysAmount === 1 && pressedKeys.has('f2');
    }, [pressedKeys, keysAmount]);
    // FYI: Zone: Go to Results
    const isF3Pressed = useMemo(() => {
        return keysAmount === 1 && pressedKeys.has('f3');
    }, [pressedKeys, keysAmount]);
    // FYI: Zone: Deletion element
    const isDeletePressed = useMemo(() => {
        return keysAmount === 1 && pressedKeys.has(deleteKeyCode.toLowerCase());
    }, [pressedKeys, keysAmount, deleteKeyCode]);
    // FYI: Form: Toggle field inspector
    const isTildaPressed = useMemo(() => {
        return pressedKeys.has('~');
    }, [pressedKeys]);
    const clearKeysPressedKeys = useCallback(() => {
        keys.current = new Set();
        setPressedKeys(new Set());
    }, []);
    useEffect(() => {
        const handleKeyDown = (e) => {
            const key = e.key.toLowerCase();
            if (!keys.current.has(key) && !SKIP_KEYS.includes(key)) {
                keys.current.add(key);
                setPressedKeys(new Set(keys.current));
            }
        };
        const handleKeyUp = (e) => {
            const key = e.key.toLowerCase();
            if (keys.current.has(key)) {
                keys.current.delete(key);
                setPressedKeys(new Set(keys.current));
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('visibilitychange', clearKeysPressedKeys);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('visibilitychange', clearKeysPressedKeys);
        };
    }, [clearKeysPressedKeys]);
    return {
        pressedKeys,
        pressedNumber,
        isF1Pressed,
        isF2Pressed,
        isF3Pressed,
        isTildaPressed,
        isDeletePressed,
        isControlIPressed,
        isControlEPressed,
        isControlLPressed,
        isControlNumberPressed,
        clearKeysPressedKeys
    };
};
export { useKeyPress };
//# sourceMappingURL=useKeyPress.js.map